import { v4 as uuid } from 'uuid';

import { useApi } from '../../store/SelectedHomeyStore';
import { useI18n } from '../../hooks/useI18nFormatters';
import { useFlowFolder } from '../../store/flow-folders/useFlowFolders';
import { useIsAltPressed } from '../../hooks/useAltKeyPressed';

import { flowFoldersDraftStore } from '../../store/flow-folders/FlowFoldersDraftStore';
import { ToastManager } from '../../ToastManager';
import { RouteManager } from '../../RouteManager';

import { ContextMenuContent } from '../../components/common/context-menu/ContextMenuContent';
import { DeveloperMenu } from '../../components/common/context-menu/DeveloperMenu';

import { iconTrashFill } from '../../theme/icons/interface/trash-fill/trash-fill';
import { iconFlow } from '../../theme/icons-v2/system/flow/flow';
import { iconAdvancedFlow } from '../../theme/icons-v2/system/advanced-flow/advanced-flow';
import { iconFolder } from '../../theme/icons/interface/folder';
import { iconUpload } from '../../theme/icons/interface/upload';

export function FlowNavigationItemFlowFolderContextMenuContent(props) {
  const { i18n } = useI18n();

  const { api } = useApi();

  const isAltPressed = useIsAltPressed();

  // const ctx = useContextMenuContext();

  const hasFolderChildren = props.flowFolder?.initialFolderChildren?.length > 0;
  const hasFlowChildren = props.flowFolder?.initialFlowChildren?.length > 0;

  // the prop.folder is not the store folder but an extended object with for example initialFolderChildren
  const { flowFolder } = useFlowFolder({ flowFolderId: props.flowFolder?.id });

  const hasAdvancedFlowLicense = api?.features.advancedFlow.hasLicense;
  const hasAdvancedFlowVersion = api?.features.advancedFlow.hasVersion;

  async function onAction(key) {
    switch (key) {
      case 'new_flow':
        if (props.flowFolder.id != null) {
          RouteManager.toFlow(`create?id=${uuid()}&folderId=${props.flowFolder.id}`);
          return;
        }

        // root folder
        RouteManager.toFlow(`create?id=${uuid()}`);
        break;
      case 'new_advanced_flow':
        if (api.isCloudAndPremium === true) {
          RouteManager.toAdvancedFlow(`create?id=${uuid()}`);
          return;
        }

        if (api.isCloud === true) {
          RouteManager.toFlows(RouteManager.getStateForPremiumRequiredDialog());
          return;
        }

        if (hasAdvancedFlowLicense === false) {
          RouteManager.toNewFlow({ viewId: 'purchase' });
          return;
        }

        if (hasAdvancedFlowVersion === false) {
          RouteManager.toNewFlow({ viewId: 'update' });
          return;
        }

        if (props.flowFolder.id != null) {
          RouteManager.toAdvancedFlow(`create?id=${uuid()}&folderId=${props.flowFolder.id}`);
          return;
        }

        // root folder
        RouteManager.toAdvancedFlow(`create?id=${uuid()}`);
        break;
      case 'new_folder':
        props.expandNode({ nodeId: props.flowFolder.id });

        flowFoldersDraftStore.setFlowFoldersDraft({
          flowFolders: {
            'tmp-id': {
              id: 'tmp-id',
              name: '',
              parent: props.flowFolder.id,
            },
          },
        });

        break;
      case 'import':
        try {
          let filedata;

          if (typeof window.showOpenFilePicker === 'function') {
            const [fileHandle] = await window.showOpenFilePicker({
              suggestedName: 'Import Flow',
              types: [
                {
                  description: 'Homey Flow',
                  accept: {
                    'application/json': ['.homeystdflow', '.homeyadvflow'],
                  },
                },
              ],
            });

            // Parse as JSON
            const file = await fileHandle.getFile();
            const contents = await file.text();
            filedata = JSON.parse(contents);
          } else {
            filedata = await new Promise((resolve, reject) => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = ['.homeystdflow', '.homeyadvflow'];
              input.addEventListener('change', () => {
                const [file] = input.files;
                if (!file) return reject(new Error('No file selected.'));

                resolve(
                  new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                      resolve(JSON.parse(reader.result));
                    };
                    reader.readAsText(file);
                  })
                );
              });
              input.click();
            });
          }

          // Create a new Flow
          switch (filedata.type) {
            case 'standard':
              api.flow
                .createFlow({
                  flow: {
                    name: filedata.name,
                    trigger: filedata.trigger,
                    conditions: filedata.conditions,
                    actions: filedata.actions,
                    folder: props.flowFolder.id,
                  },
                })
                .then((flow) => RouteManager.toFlow(flow.id))
                .catch(ToastManager.handleError);
              break;
            case 'advanced':
              api.flow
                .createAdvancedFlow({
                  advancedflow: {
                    name: filedata.name,
                    cards: filedata.cards,
                    folder: props.flowFolder.id,
                  },
                })
                .then((advancedFlow) => RouteManager.toAdvancedFlow(advancedFlow.id))
                .catch(ToastManager.handleError);
              break;
            default:
              throw new Error('Invalid Flow Format.');
          }
        } catch (err) {
          if (err.message.includes('The user aborted a request.')) return;
          if (err.message.includes('No file selected.')) return;

          console.error(err);
          alert(err.message);
        }

        break;
      case 'rename':
        props.onRenameRequest();
        break;
      case 'delete':
        api.flow
          .deleteFlowFolder({
            id: props.flowFolder.id,
          })
          .catch(ToastManager.handleError);
        //props.onDeleteRequest();
        break;
      default:
        break;
    }
  }

  return (
    <ContextMenuContent.List>
      <ContextMenuContent.ListItem
        label={i18n.messageFormatter('flow.newFolder')}
        icon={iconFolder}
        onPress={() => {
          onAction('new_folder');
        }}
      />
      <ContextMenuContent.ListItem
        label={i18n.messageFormatter('flow.newFlow')}
        icon={iconFlow}
        onPress={() => {
          onAction('new_flow');
        }}
      />

      <ContextMenuContent.ListItem
        label={i18n.messageFormatter('flow.newAdvancedFlow')}
        icon={iconAdvancedFlow}
        onPress={() => {
          onAction('new_advanced_flow');
        }}
      />

      {isAltPressed === true && (
        <ContextMenuContent.ListItem
          label={i18n.messageFormatter('flow.import')}
          icon={iconUpload}
          onPress={() => {
            onAction('import');
          }}
        />
      )}

      {props.flowFolder.id !== null && (
        <>
          <ContextMenuContent.Divider />

          <ContextMenuContent.ListItem
            label={i18n.messageFormatter('common.rename')}
            onPress={() => {
              onAction('rename');
            }}
          />

          <DeveloperMenu
            onIdRequest={() => props.flowFolder?.id}
            onResourceRequest={() => flowFolder}
          />

          <ContextMenuContent.Divider />
          <ContextMenuContent.ListItem
            label={i18n.messageFormatter('common.delete')}
            variant="danger"
            icon={iconTrashFill}
            isDisabled={hasFolderChildren || hasFlowChildren}
            tooltip={
              (hasFolderChildren || hasFlowChildren) && i18n.messageFormatter('flow.folderNotEmpty')
            }
            onPress={() => {
              onAction('delete');
            }}
          />
        </>
      )}
    </ContextMenuContent.List>
  );
}
